import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { FileText, Upload, MessageSquareDashed, File, X } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ManualKBCreation } from "@/components/ManualKBCreation";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { extractContentFromFile } from "@/utils/universalContentExtractor";
import teamHandsIcon from "@/assets/team-hands-icon.png";
import gibletOwlWhite from "@/assets/giblet-owl-white.png";
import gibletNoEyes from "@/assets/giblet-no-eyes.png";
import { ChannelSelector } from "@/components/channels/ChannelSelector";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Badge } from "@/components/ui/badge";
import { CreatorsGibletChatBar } from "@/components/chat/CreatorsGibletChatBar";
import { GibletChatPanel } from "@/components/chat/GibletChatPanel";
import { EngageAction } from "@/components/chat/GibletEngageMenu";
import { ScrollArea } from "@/components/ui/scroll-area";

const Creators = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isCompositionEnabled, isConsumptionEnabled } = useFeatureFlags();
  const [createMethod, setCreateMethod] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [gibletDescription, setGibletDescription] = useState<string>("");
  const [chatActive, setChatActive] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [pendingInitialMessage, setPendingInitialMessage] = useState<string | null>(null);
  const [pendingEngageAction, setPendingEngageAction] = useState<EngageAction | null>(null);
  const [chatSessionKey, setChatSessionKey] = useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Refs for eye tracking
  const containerRef = useRef<HTMLDivElement>(null);
  const leftEyeRef = useRef<SVGCircleElement>(null);
  const rightEyeRef = useRef<SVGCircleElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Mouse position state for eye tracking
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Eye socket positions (same as Index.tsx)
  const leftEyeBaseX = 109;
  const leftEyeBaseY = 45;
  const rightEyeBaseX = 189;
  const rightEyeBaseY = 45;
  const eyeSize = 36;
  const pupilSize = 25;
  const maxOffset = 12;
  const movementDivisor = 18;

  // Reset to main view when navigated with reset state
  useEffect(() => {
    if (location.state?.reset) {
      setCreateMethod(null);
      setSelectedFiles([]);
      setGibletDescription("");
      setSelectedChannelId(null);
      setChatActive(false);
      setChatInput('');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.reset, location.pathname, navigate]);

  // Handle incoming initialMessage or engageAction from navbar
  useEffect(() => {
    if (location.state?.initialMessage) {
      const msg = location.state.initialMessage;
      setChatActive(true);
      setPendingInitialMessage(msg);
      setChatSessionKey(prev => prev + 1);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.engageAction) {
      const action = location.state.engageAction;
      setChatActive(true);
      setPendingEngageAction(action);
      setChatSessionKey(prev => prev + 1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.initialMessage, location.state?.engageAction, location.pathname, navigate]);

  // Fetch available channels
  const { data: channels = [] } = useQuery({
    queryKey: ['channels'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .order('name');
      if (error) throw error;
      return data;
    }
  });

  // Track mouse movement for eye animation
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Update eye positions based on mouse
  useEffect(() => {
    if (!svgRef.current || !leftEyeRef.current || !rightEyeRef.current) return;

    const svgRect = svgRef.current.getBoundingClientRect();

    const calculateEyePosition = (eyeBaseX: number, eyeBaseY: number) => {
      const eyeCenterX = svgRect.left + eyeBaseX + eyeSize / 2;
      const eyeCenterY = svgRect.top + eyeBaseY + eyeSize / 2;
      
      const deltaX = mousePosition.x - eyeCenterX;
      const deltaY = mousePosition.y - eyeCenterY;
      const angle = Math.atan2(deltaY, deltaX);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      const movement = Math.min(distance / movementDivisor, maxOffset);
      
      let offsetX = Math.cos(angle) * movement;
      let offsetY = Math.sin(angle) * movement;
      
      // Constrain within circular bounds
      const offsetMagnitude = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
      if (offsetMagnitude > maxOffset) {
        offsetX = (offsetX / offsetMagnitude) * maxOffset;
        offsetY = (offsetY / offsetMagnitude) * maxOffset;
      }
      
      return { x: eyeBaseX + offsetX, y: eyeBaseY + offsetY };
    };

    const leftPos = calculateEyePosition(leftEyeBaseX, leftEyeBaseY);
    const rightPos = calculateEyePosition(rightEyeBaseX, rightEyeBaseY);

    leftEyeRef.current.setAttribute('cx', String(leftPos.x + eyeSize / 2));
    leftEyeRef.current.setAttribute('cy', String(leftPos.y + eyeSize / 2));
    rightEyeRef.current.setAttribute('cx', String(rightPos.x + eyeSize / 2));
    rightEyeRef.current.setAttribute('cy', String(rightPos.y + eyeSize / 2));
  }, [mousePosition]);

  const resetDemo = () => {
    setCreateMethod(null);
    setSelectedFiles([]);
  };

  const handleFileImport = () => {
    toast({
      title: "File import started",
      description: "Your documents are being processed"
    });
    setCreateMethod("import");
  };

  const handleGibletCreate = () => {
    toast({
      title: "Giblet assistance requested",
      description: "Giblet will help you create your knowledge base"
    });
    setCreateMethod("giblet");
  };


  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...files]);
      toast({
        title: `Selected ${files.length} file(s)`,
        description: files.map(file => file.name).join(", ")
      });
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  return <div className="min-h-screen bg-white">
      <Navbar>
        {/* Create/Compose/Consume buttons centered in navbar */}
        {!chatActive && (
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button className="bg-black text-white text-sm font-medium px-4 py-1.5 rounded hover:bg-black/80 transition-colors cursor-pointer">
                  Create
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="center" sideOffset={8} avoidCollisions={false} className="max-w-sm text-sm p-4">
                <p>Build and curate knowledge bases as the contextual assets that capture expertise, insights, and domain knowledge from your lived experience.</p>
              </PopoverContent>
            </Popover>
            
            <Popover>
              <PopoverTrigger asChild>
                <button className={`text-sm font-medium px-4 py-1.5 rounded transition-colors ${isCompositionEnabled ? 'bg-black text-white hover:bg-black/80 cursor-pointer' : 'bg-gray-400 text-white cursor-not-allowed'}`}>
                  Compose
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="center" sideOffset={8} avoidCollisions={false} className="max-w-sm text-sm p-4">
                <p>Assemble knowledge bases into comprehensive solutions that address complex enterprise challenges through structured collaboration and AI-assisted orchestration.</p>
              </PopoverContent>
            </Popover>
            
            <Popover>
              <PopoverTrigger asChild>
                <button className={`text-sm font-medium px-4 py-1.5 rounded transition-colors ${isConsumptionEnabled ? 'bg-black text-white hover:bg-black/80 cursor-pointer' : 'bg-gray-400 text-white cursor-not-allowed'}`}>
                  Consume
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="center" sideOffset={8} avoidCollisions={false} className="max-w-sm text-sm p-4">
                <p>Access the marketplace where knowledge bases and solutions can be purchased, licensed, staked for collaboration, or retired.</p>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </Navbar>
      <main className="container mx-auto px-4 flex flex-col gap-8" style={{ minHeight: 'calc(100vh - 64px)' }}>
        
        {/* Roaming Eyes Giblet */}
        <div className="flex-[1] flex items-center justify-center pt-4" ref={containerRef}>
          <motion.div 
            className="relative float-animation" 
            initial={{ scale: 0.8, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
          >
            <svg ref={svgRef} width="334" height="206" viewBox="0 0 334 206" className="animate-fade-in">
              <image href={gibletNoEyes} width="334" height="206" x="0" y="0" />
              <circle 
                ref={leftEyeRef}
                cx={leftEyeBaseX + eyeSize / 2} 
                cy={leftEyeBaseY + eyeSize / 2} 
                r={pupilSize / 2} 
                fill="black"
                style={{ transition: 'all 0.15s ease-out' }}
              />
              <circle 
                ref={rightEyeRef}
                cx={rightEyeBaseX + eyeSize / 2} 
                cy={rightEyeBaseY + eyeSize / 2} 
                r={pupilSize / 2} 
                fill="black"
                style={{ transition: 'all 0.15s ease-out' }}
              />
            </svg>
          </motion.div>
        </div>

        {/* Chat Bar + Chat Thread */}
        <div className="flex-[2] pb-8 pt-4">
          {!chatActive && (
            <div className="mb-6">
              <CreatorsGibletChatBar
                value={chatInput}
                onChange={setChatInput}
                onSend={() => {
                  if (!chatInput.trim()) return;
                  setChatActive(true);
                  setPendingInitialMessage(chatInput);
                  setChatSessionKey(prev => prev + 1);
                  setChatInput('');
                }}
                onEngageAction={(action) => {
                  if (action === 'consume') {
                    navigate('/ispai-solutions');
                    return;
                  }
                  if (action === 'compose') {
                    navigate('/prompt-architecture-sprint');
                    return;
                  }
                  setChatActive(true);
                  setPendingEngageAction(action);
                  setChatSessionKey(prev => prev + 1);
                }}
                isLoading={false}
                connectedKBsCount={0}
                maxKBs={50}
              />
            </div>
          )}

          {chatActive && (
            <div className="mx-auto max-w-2xl flex-1 h-[calc(100vh-200px)]">
              <GibletChatPanel 
                key={chatSessionKey}
                initialCallType={null}
                initialMessage={pendingInitialMessage}
                initialEngageAction={pendingEngageAction}
                onClose={() => setChatActive(false)}
              />
            </div>
          )}
        </div>
      </main>
    </div>;
};

export default Creators;
